/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';
import MetadataToQueryTypeMap from '../../enums/MetadataToQueryTypeMap';
// @ts-ignore
import queryVariableTypes from '../../api/model/enum/QueryVariableTypes';

const relativeDataRegExp = /^(DAY|WEEK|MONTH|QUARTER|SEMI|YEAR)([+|-][\d]{1,9})?$/;

function toNode(serverNode: any) {
    const key = _.keys(serverNode)[0];
    let res = _.omit(serverNode[key], 'operands', 'start', 'end');
    res.nodeType = key;
    if (key === 'range') {
        res.operands = [
            toNode(serverNode[key].start),
            toNode(serverNode[key].end)
        ];
    } else if (key === 'boundary') {
        res = toNode(serverNode[key]);
    } else if (serverNode[key].operands) {
        res.operands = _.map(serverNode[key].operands, toNode);
    }
    if (serverNode[key].value) {
        res.nodeType = 'literal';
        res.type = key;
    }
    return res;
}

function findNodesOfType(root: any, type: any, memo: any[] = []) {
    if (root.nodeType === type) {
        memo.push(root);
    }
    if (root.operands) {
        for (let i = 0; i < root.operands.length; i += 1) {
            findNodesOfType(root.operands[i], type, memo);
        }
    }
    return memo;
}

function ensureTypes(parameters: any, types: any = {}) {
    return _.reduce(parameters, (mm: any, parameter: any) => {
        const memo = mm;
        if (!memo[parameter.name]) {
            const expr = parameter.expression.object.list ? parameter.expression.object.list.items[0] : parameter.expression.object;
            if (expr) {
                const [name] = _.keys(expr);
                memo[parameter.name] = name;
            } else {
                memo[parameter.name] = 'string';
            }
        }
        return memo;
    }, types);
}

// @ts-ignore unused
function determineParameterValue(parameter: any): any {
    if (parameter.expression.object.list) {
        if (parameter.expression.object.list.items.length) {
            return _.map(parameter.expression.object.list.items, (item) => {
                return _.values(item)[0].value;
            });
        }
        return [];

    }
    return _.values(parameter.expression.object)[0].value;

}

function setNullValues(type: any, value: any) {
    let res = value;
    if (value === '' && type !== 'string') {
        res = null;
    } else if (value === '~NULL~') {
        res = null;
    }
    return res;
}

function getByReference(group: any, reference: any): any {
    let res;
    for (let i = group.length - 1; i > -1 && !res; i -= 1) {
        if (group[i].group) {
            res = getByReference(group[i].group.elements, reference);
        } else if (group[i].element) {
            if (group[i].element.hierarchicalName === reference) {
                res = group[i].element;
            }
        } else {
            throw new Error('Unknown element type in schema');
        }
    }
    return res;
}

function readFilterInformation(filterExpression: any, schema: any) {
    const filters = _.filter(findNodesOfType(filterExpression, 'function'), (fun) => {
        return fun.functionName === 'filter';
    });

    return filters
        .filter((filter) => (filter.operands[1].value === 'DYNAMIC'))
        .map((filter) => {
            const variables = findNodesOfType(filter.operands[2], 'variable');
            const type = variables.reduce((mm, variable) => {
                let memo = mm;
                if (!memo) {
                    const levelData = getByReference(schema.presentation, variable.name);
                    if (levelData) {
                        memo = MetadataToQueryTypeMap[levelData.type] || levelData.type;
                    }
                }
                return memo;
            }, null);

            return {
                id: filter.operands[0].value,
                isDynamic: filter.operands[1].value === 'DYNAMIC',
                levelName: filter.operands[2].operands[0].name,
                filterExpression: filter.operands[2],
                variables: variables.map((variable) => ({
                    name: variable.name,
                    type
                }))
            };
        });
}

function getActualFilterParameters(parameters: any, altTypes: any) {
    const singular = _.reduce(parameters, (mm: any, parameter: any) => {
        const memo = mm;
        memo[parameter.name] = !parameter.expression.object.list;
        return memo;
    }, {} as any);

    const types = ensureTypes(parameters, altTypes);

    return parameters.map((parameter: any) => ({
        name: parameter.name,
        // @ts-ignore
        type: types[parameter.name],
        singleValue: singular[parameter.name],
        value: parameter.expression
    }));
}

function toServerValue(type: string, val: string) {
    const value = setNullValues(type, val);
    const res: any = {};
    if (value === null) {
        res.NULL = {};
    } else if ((type === queryVariableTypes.DATE || type === queryVariableTypes.TIMESTAMP) && relativeDataRegExp.test(value)) {
        if (type === queryVariableTypes.DATE) {
            res.relativeDateRange = { value };
        } else {
            res.relativeTimestampRange = { value };
        }
    } else if (type === queryVariableTypes.TIMESTAMP) {
        res[type] = { value: value.replace('T', ' ') };
    } else {
        res[type] = { value };
    }
    return res;
}

export function getFilterExpression(queryPart: any) {
    const query = queryPart.multiLevel || queryPart.multiAxis;
    return query && query.where && query.where.filterExpression;
}

function getFilterParametersObject(queryPart: any) {
    const query = queryPart.multiLevel || queryPart.multiAxis;
    return query && query.where && query.where.parameters;
}
export function getFilterParameters(queryPart: any, schemaPart: any) {
    const filterExpression = getFilterExpression(queryPart);
    const filterParameters = getFilterParametersObject(queryPart);
    let res;

    if (filterParameters) {
        let altTypes = {};

        if (filterExpression) {
            const expressionTree = toNode(filterExpression.object);
            const filtersMetadata = readFilterInformation(expressionTree, schemaPart);

            altTypes = filtersMetadata.reduce((memo, filter) => {
                return filter.variables.reduce((mm, variable) => {
                    const mem = mm;
                    mem[variable.name] = variable.type;
                    return mem;
                }, memo);
            }, altTypes as any);
        }

        res = getActualFilterParameters(filterParameters, altTypes);
    } else {
        res = [];
    }

    return res;
}

export function updateFilterParameters(parameters: any[], params: any) {
    return parameters.map((parameter) => {
        let newParameter = parameter;
        if (params[parameter.name]) {
            newParameter = { ...parameter };
            if (parameter.singleValue) {
                newParameter.value = { object: toServerValue(parameter.type, params[parameter.name][0]) };
            } else {
                newParameter.value = { object: { list: { items: _.map(params[parameter.name], _.bind(toServerValue, null, parameter.type)) } } };
            }
        }
        return newParameter;
    })
}
